(() => {
  const namespaceKey = 'GuruShopMinGuruMemberCart';
  const existingApi = window[namespaceKey];

  if (existingApi?.refresh) {
    existingApi.refresh();
    return;
  }

  const portalOrigin = 'https://min.gurushop.dk';
  const portalPath = '/min-guru';
  const rowSelector = '[data-min-guru-member-discount]';
  const priceLadderSelector = '[data-min-guru-member-pricing]';
  const cartRootSelector = '[data-guru-cart-drawer], [data-guru-cart-page]';
  const memberDiscountTitles = [
    'Min Guru medlemsrabat',
    'Min Guru special produktrabat',
  ];
  let authenticated = false;
  let latestDiscountAmount = 0;
  let latestMemberLines = [];
  let latestMemberTotal = null;
  let latestCheckoutUrl = '';
  let latestStackingBlocked = false;
  let scheduledSync;
  let syncSequence = 0;
  let activeSyncController = null;
  let activeSyncRoots = [];
  let checkoutInFlight = null;
  let checkoutAttempt = 0;
  let cartRevision = 0;
  let sessionResolved = false;
  let activeCommerceOperations = 0;
  let drawerRefreshPending = false;

  const isMemberDiscountTitle = (title) => {
    const normalizedTitle = typeof title === 'string' ? title.trim() : '';
    return (
      memberDiscountTitles.some(
        (candidate) =>
          normalizedTitle === candidate ||
          normalizedTitle.startsWith(`${candidate} (`),
      ) ||
      /^Min Guru · \d+% specialrabat(?:\s|$)/.test(normalizedTitle)
    );
  };

  const nativeMemberDiscountAmount = (cart) =>
    (cart?.items || []).reduce(
      (total, item) =>
        total +
        (item.line_level_discount_allocations || []).reduce(
          (lineTotal, allocation) =>
            isMemberDiscountTitle(allocation.discount_application?.title)
              ? lineTotal + Number(allocation.amount || 0)
              : lineTotal,
          0,
        ),
      0,
    );

  const lineMemberDiscountAmount = (allocations) =>
    (allocations || []).reduce(
      (total, allocation) =>
        isMemberDiscountTitle(allocation.title)
          ? total + Number(allocation.amount || 0)
          : total,
      0,
    );

  const hasUnsupportedCartState = (cart) => {
    if (!Array.isArray(cart?.items)) return true;
    if (cart.items.length > 50) return true;
    if (typeof cart.note === 'string' && cart.note.trim()) return true;

    const hasAttributes = Object.entries(cart.attributes || {}).some(
      ([key, value]) =>
        !key.startsWith('products_') &&
        typeof value === 'string' &&
        value.trim(),
    );
    if (hasAttributes) return true;

    const hasOtherDiscount = (cart.discount_applications || []).some(
      (discount) => !isMemberDiscountTitle(discount.title),
    );
    if (hasOtherDiscount || (cart.discount_codes || []).length > 0) return true;

    const identities = new Set();
    return cart.items.some((item) => {
      if (item.selling_plan_allocation) return true;
      const variantId = Number(item.variant_id);
      const quantity = Number(item.quantity);
      if (
        !Number.isInteger(variantId) ||
        variantId < 1 ||
        !Number.isInteger(quantity) ||
        quantity < 1 ||
        quantity > 50
      ) {
        return true;
      }
      const hasProperties = Object.entries(item.properties || {}).some(
        ([, value]) =>
          value !== null &&
          value !== undefined &&
          String(value).trim(),
      );
      const hasOtherLineDiscount = (
        item.line_level_discount_allocations || []
      ).some(
        (allocation) =>
          !isMemberDiscountTitle(
            allocation.discount_application?.title,
          ),
      );
      const commerceIdentity = JSON.stringify([
        String(item.variant_id || ''),
        String(item.selling_plan_allocation?.selling_plan?.id || ''),
        Object.entries(item.properties || {}).sort(([a], [b]) =>
          a.localeCompare(b),
        ),
      ]);
      const isDuplicateIdentity = identities.has(commerceIdentity);
      identities.add(commerceIdentity);
      return hasProperties || hasOtherLineDiscount || isDuplicateIdentity;
    });
  };

  const portalLines = (cart) =>
    (cart.items || []).map((item) => ({
      merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
      quantity: Number(item.quantity),
    }));

  const memberDiscountAmount = (cart) =>
    (cart?.lines || []).reduce(
      (total, line) =>
        total + lineMemberDiscountAmount(line.discountAllocations),
      0,
    );

  const roundCurrency = (amount) => Math.round(amount * 100) / 100;

  const formatAmountToken = (amount, token) => {
    const noDecimals = token.includes('no_decimals');
    const comma = token.includes('comma_separator');
    const space = token.includes('space_separator');
    const apostrophe = token.includes('apostrophe_separator');
    const decimal = comma ? ',' : '.';
    const thousands = comma ? '.' : space ? ' ' : apostrophe ? "'" : ',';
    const fixed = Number(amount || 0).toFixed(noDecimals ? 0 : 2);
    const [whole, fraction] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return noDecimals ? grouped : `${grouped}${decimal}${fraction}`;
  };

  const money = (amount) => {
    const settings = window.themeVariables?.settings || {};
    const format = settings.currencyCodeEnabled
      ? settings.moneyWithCurrencyFormat
      : settings.moneyFormat;
    const match = typeof format === 'string'
      ? format.match(/\{\{\s*([a-z_]+)\s*\}\}/i)
      : null;
    if (match) return format.replace(match[0], formatAmountToken(amount, match[1]));
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: 'DKK',
      currencyDisplay: settings.currencyCodeEnabled ? 'code' : 'symbol',
    }).format(amount);
  };

  const aggregateSavingsMinor = ({
    referenceMinor,
    nativeTotalMinor,
    memberTotal = null,
    memberDiscount = 0,
  }) => {
    const finalTotalMinor = memberTotal !== null && memberDiscount > 0
      ? Math.round(memberTotal * 100)
      : Number(nativeTotalMinor || 0);
    return Math.max(0, Number(referenceMinor || 0) - finalTotalMinor);
  };

  const numericVariantId = (value) =>
    String(value || '').match(/ProductVariant\/(\d+)$/)?.[1] ||
    String(value || '');

  const normalizedProperties = (value) => {
    const source = Array.isArray(value)
      ? value.map((entry) => [entry?.key ?? entry?.name, entry?.value])
      : Object.entries(value || {});
    const entries = [];
    const keys = new Set();
    for (const [key, propertyValue] of source) {
      if (typeof key !== 'string' || !key || keys.has(key)) return null;
      keys.add(key);
      if (
        propertyValue === null ||
        propertyValue === undefined ||
        String(propertyValue).trim() === ''
      ) continue;
      entries.push([key, String(propertyValue)]);
    }
    return JSON.stringify(
      entries.sort(([left], [right]) => left.localeCompare(right)),
    );
  };

  const nativeQuoteIdentity = (item) => {
    const variantId = numericVariantId(item?.variant_id);
    const sellingPlanId = String(
      item?.selling_plan_allocation?.selling_plan?.id || '',
    );
    const properties = normalizedProperties(item?.properties);
    if (!/^\d+$/.test(variantId) || properties === null) return null;
    return JSON.stringify([variantId, sellingPlanId, properties]);
  };

  const portalQuoteIdentity = (line) => {
    const variantId = numericVariantId(line?.variantId);
    const sellingPlanId = String(line?.sellingPlan?.id || '');
    const properties = normalizedProperties(
      line?.properties ?? line?.attributes ?? {},
    );
    if (!/^\d+$/.test(variantId) || properties === null) return null;
    return JSON.stringify([variantId, sellingPlanId, properties]);
  };

  const quoteLineMap = (lines, identityFor) => {
    const mapped = new Map();
    for (const line of lines || []) {
      const identity = identityFor(line);
      const quantity = Number(line?.quantity);
      if (
        !identity ||
        !Number.isSafeInteger(quantity) ||
        quantity < 1 ||
        mapped.has(identity)
      ) return null;
      mapped.set(identity, quantity);
    }
    return mapped;
  };

  const quoteLinesMatch = (nativeCart, memberCart) => {
    const nativeLines = quoteLineMap(nativeCart?.items, nativeQuoteIdentity);
    const memberLines = quoteLineMap(memberCart?.lines, portalQuoteIdentity);
    if (!nativeLines || !memberLines || nativeLines.size !== memberLines.size) {
      return false;
    }
    return [...nativeLines].every(
      ([identity, quantity]) => memberLines.get(identity) === quantity,
    );
  };

  const quoteLineTotalsMatch = (nativeCart, memberCart) => {
    const memberByIdentity = new Map();
    for (const line of memberCart?.lines || []) {
      const identity = portalQuoteIdentity(line);
      if (!identity || memberByIdentity.has(identity)) return false;
      const allocations = line?.discountAllocations || [];
      if (allocations.some((allocation) => !isMemberDiscountTitle(allocation?.title))) {
        return false;
      }
      memberByIdentity.set(identity, line);
    }

    return (nativeCart?.items || []).every((item) => {
      const identity = nativeQuoteIdentity(item);
      const memberLine = memberByIdentity.get(identity);
      const quantity = Number(memberLine?.quantity);
      const memberLineTotalMinor = exactLineTotalMinor(memberLine);
      const discountMinor = minorAmount(
        lineMemberDiscountAmount(memberLine?.discountAllocations),
      );
      const nativeLineTotal = Number(item?.final_line_price);
      return (
        Number.isSafeInteger(quantity) &&
        quantity > 0 &&
        memberLineTotalMinor !== null &&
        discountMinor !== null &&
        discountMinor >= 0 &&
        Number.isSafeInteger(nativeLineTotal) &&
        nativeLineTotal === memberLineTotalMinor + discountMinor
      );
    });
  };

  const minorAmount = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? Math.round(amount * 100) : null;
  };

  const exactLineTotalMinor = (line) => {
    const amount = Number(line?.lineTotalMinor);
    return Number.isSafeInteger(amount) && amount >= 0 ? amount : null;
  };

  const approvedStorefrontOrigin = () => {
    const configured = document.querySelector(
      `${cartRootSelector}[data-cart-storefront-origin]`,
    )?.dataset.cartStorefrontOrigin;
    try {
      const url = new URL(configured || '');
      if (
        url.protocol !== 'https:' ||
        url.username ||
        url.password ||
        url.hash
      ) return '';
      return url.origin;
    } catch {
      return '';
    }
  };

  const validMemberCheckoutUrl = (value) => {
    if (typeof value !== 'string' || !value || value !== value.trim()) return '';
    try {
      const url = new URL(value);
      const approvedOrigin = approvedStorefrontOrigin();
      const rawTarget = approvedOrigin && value.startsWith(`${approvedOrigin}/`)
        ? value.slice(approvedOrigin.length)
        : '';
      const parameters = [...url.searchParams.entries()];
      if (
        !approvedOrigin ||
        !rawTarget ||
        url.protocol !== 'https:' ||
        url.origin !== approvedOrigin ||
        url.username ||
        url.password ||
        url.hash ||
        !/^\/cart\/c\/[A-Za-z0-9._~-]+\?key=[^&#\s]+$/.test(rawTarget) ||
        !/^\/cart\/c\/[A-Za-z0-9._~-]+$/.test(url.pathname) ||
        !/^\?key=[^&]+$/.test(url.search) ||
        parameters.length !== 1 ||
        parameters[0][0] !== 'key' ||
        parameters[0][1].trim() === ''
      ) return '';
      return value;
    } catch {
      return '';
    }
  };

  const validateMemberQuote = (memberCart, nativeCart) => {
    const nativeCurrency = String(nativeCart?.currency || '').toUpperCase();
    const memberCurrency = String(
      memberCart?.commerce?.currencyCode || '',
    ).toUpperCase();
    if (!nativeCurrency || nativeCurrency !== memberCurrency) return null;
    if (!quoteLinesMatch(nativeCart, memberCart)) return null;
    if (!quoteLineTotalsMatch(nativeCart, memberCart)) return null;

    const nativeTotal = Number(nativeCart?.total_price);
    const nativeLineTotal = (nativeCart?.items || []).reduce(
      (total, item) => total + Number(item?.final_line_price),
      0,
    );
    if (
      !Number.isSafeInteger(nativeTotal) ||
      nativeTotal < 0 ||
      !(nativeCart?.items || []).every((item) =>
        Number.isSafeInteger(Number(item?.final_line_price)),
      ) ||
      nativeLineTotal !== nativeTotal
    ) return null;

    const memberSubtotalMinor = minorAmount(memberCart?.subtotal);
    const memberLineTotalMinor = (memberCart?.lines || []).reduce(
      (total, line) => {
        const exactTotal = exactLineTotalMinor(line);
        return exactTotal === null
          ? Number.NaN
          : total + exactTotal;
      },
      0,
    );
    if (
      memberSubtotalMinor === null ||
      memberSubtotalMinor < 0 ||
      !Number.isSafeInteger(memberLineTotalMinor) ||
      memberLineTotalMinor !== memberSubtotalMinor
    ) return null;

    const discountAmount = memberDiscountAmount(memberCart);
    const discountMinor = minorAmount(discountAmount);
    if (
      !Number.isFinite(discountAmount) ||
      discountAmount < 0 ||
      discountMinor === null ||
      memberSubtotalMinor + discountMinor !== nativeTotal
    ) return null;
    const checkoutUrl = validMemberCheckoutUrl(memberCart?.checkoutUrl);
    if (discountAmount > 0 && !checkoutUrl) return null;
    return { checkoutUrl, discountAmount, subtotal: memberSubtotalMinor / 100 };
  };

  const portalMemberLines = (cart, nativeCart) => {
    const nativeByVariant = new Map();
    for (const item of nativeCart?.items || []) {
      const id = String(item.variant_id || '');
      if (nativeByVariant.has(id)) nativeByVariant.set(id, null);
      else nativeByVariant.set(id, String(item.key || ''));
    }
    return (cart?.lines || [])
      .map((line) => {
        const quantity = Math.max(1, Number(line.quantity || 1));
        const discountAmount = lineMemberDiscountAmount(
          line.discountAllocations,
        );
        const memberLineTotalMinor = exactLineTotalMinor(line);
        const memberPrice = Number(line.price || 0);
        if (
          discountAmount <= 0 ||
          memberPrice < 0 ||
          memberLineTotalMinor === null
        ) return null;
        const memberLineTotal = memberLineTotalMinor / 100;
        const shopPrice = roundCurrency(
          (memberLineTotal + discountAmount) / quantity,
        );
        return {
          commerceIdentity: nativeByVariant.get(numericVariantId(line.variantId)) || '',
          variantId: numericVariantId(line.variantId),
          quantity,
          memberPrice,
          memberLineTotal,
          shopPrice,
          compareAt: Math.max(Number(line.compareAt || 0), shopPrice),
          discountAmount,
        };
      })
      .filter((line) => Boolean(line?.commerceIdentity));
  };

  const nativeMemberLines = (cart) =>
    (cart?.items || [])
      .map((item) => {
        const discountAmount = (item.line_level_discount_allocations || [])
          .reduce(
            (total, allocation) =>
              isMemberDiscountTitle(
                allocation.discount_application?.title,
              )
                ? total + Number(allocation.amount || 0)
                : total,
            0,
          );
        if (discountAmount <= 0) return null;
        return {
          commerceIdentity: String(item.key || ''),
          variantId: String(item.variant_id || ''),
          quantity: Math.max(1, Number(item.quantity || 1)),
          memberPrice: Number(item.final_price || 0) / 100,
          memberLineTotal: Number(item.final_line_price || 0) / 100,
          shopPrice: Number(item.original_price || 0) / 100,
          compareAt: 0,
          discountAmount: discountAmount / 100,
        };
      })
      .filter(Boolean);

  const clearMemberPricing = ({ stackingBlocked = false } = {}) => {
    latestDiscountAmount = 0;
    latestMemberLines = [];
    latestMemberTotal = null;
    latestCheckoutUrl = '';
    latestStackingBlocked = stackingBlocked;
  };

  const initializeDrawerRoot = (root) => {
    if (!root?.matches?.(cartRootSelector)) return;
    root.dataset.minGuruSessionState = sessionResolved
      ? 'resolved'
      : 'resolving';
    root.dataset.minGuruCartMutationBusy = String(
      activeCommerceOperations > 0 || drawerRefreshPending,
    );
    root.dataset.minGuruCheckoutBusy = String(Boolean(checkoutInFlight));
  };

  const syncDrawerRuntimeState = () => {
    document.querySelectorAll(cartRootSelector).forEach(
      initializeDrawerRoot,
    );
    window.GuruShopCartDrawer?.refresh?.();
  };

  const setSessionState = (state) => {
    sessionResolved = state === 'resolved';
    syncDrawerRuntimeState();
    document.querySelectorAll('form[action*="/cart"] [name="checkout"]').forEach(
      (button) => {
        if (button.closest(cartRootSelector)) return;
        if (state === 'resolving') {
          button.dataset.minGuruSessionWasDisabled = button.disabled ? 'true' : 'false';
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
        } else if (Object.hasOwn(button.dataset, 'minGuruSessionWasDisabled')) {
          button.disabled = button.dataset.minGuruSessionWasDisabled === 'true';
          button.removeAttribute('aria-busy');
          delete button.dataset.minGuruSessionWasDisabled;
        }
      },
    );
  };

  const scopeHasNativeMemberDiscount = (container) => {
    const scope =
      container.closest(`cart-drawer, ${cartRootSelector}, .cart-page`) || document;
    return Array.from(
      scope.querySelectorAll(
        `.discount-badge:not(${rowSelector})`,
      ),
    ).some((element) => isMemberDiscountTitle(element.textContent));
  };

  const renderDiscount = () => {
    document
      .querySelectorAll('.cart-drawer__footer, .guru-cart-page .cart-panel__footer, .cart-recap')
      .forEach((container) => {
        const approvedDrawer = container.closest(cartRootSelector);
        const approvedSavings = approvedDrawer?.querySelector(
          '[data-cart-savings]',
        );
        const approvedSavingsAmount = approvedSavings?.querySelector(
          '[data-cart-savings-amount]',
        );
        const existing = container.querySelector(rowSelector);

        if (approvedSavings && approvedSavingsAmount) {
          existing?.remove();
          const referenceMinor = Number(
            approvedDrawer.dataset.cartReferenceTotal || 0,
          );
          const nativeTotalMinor = Number(
            approvedDrawer.dataset.cartNativeTotal || 0,
          );
          const totalSavingsMinor = aggregateSavingsMinor({
            referenceMinor,
            nativeTotalMinor,
            memberTotal: latestMemberTotal,
            memberDiscount: latestDiscountAmount,
          });
          if (totalSavingsMinor > 0) {
            approvedSavings.hidden = false;
            approvedSavingsAmount.textContent = `−${money(totalSavingsMinor / 100)}`;
          } else {
            approvedSavings.hidden = true;
            approvedSavingsAmount.textContent = '';
          }
          return;
        }

        if (
          latestDiscountAmount <= 0 ||
          scopeHasNativeMemberDiscount(container)
        ) {
          existing?.remove();
          return;
        }

        const row = existing || document.createElement('div');
        if (!existing) {
          row.className = 'min-guru-member-cart-discount';
          row.dataset.minGuruMemberDiscount = 'true';
          row.setAttribute('aria-live', 'polite');
          row.append(
            document.createElement('span'),
            document.createElement('strong'),
          );
          container.prepend(row);
        }
        row.querySelector('span').textContent =
          'Min Guru medlemsrabat i alt';
        row.querySelector('strong').textContent =
          `−${money(latestDiscountAmount)}`;
      });
  };

  const priceRow = (label, value, modifier = '') => {
    const row = document.createElement('div');
    row.className = `min-guru-member-price-row ${modifier}`.trim();
    const term = document.createElement('dt');
    const description = document.createElement('dd');
    term.textContent = label;
    description.textContent = money(value);
    row.append(term, description);
    return row;
  };

  const renderLinePricing = () => {
    document
      .querySelectorAll('line-item[data-min-guru-variant-id]')
      .forEach((lineItem) => {
        const memberLine = latestMemberLines.find(
          (line) => line.commerceIdentity === lineItem.dataset.commerceIdentity,
        );
        const existing = lineItem.querySelector(priceLadderSelector);
        const existingBadge = lineItem.querySelector(
          '[data-min-guru-member-savings]',
        );
        const compactPrice = lineItem.querySelector(
          '[data-guru-cart-line-price]',
        );
        const compactReference = compactPrice?.querySelector(
          '[data-guru-cart-reference]',
        );

        if (compactPrice && compactReference) {
          existing?.remove();
          existingBadge?.remove();
          const nativeReferenceVisible =
            compactPrice.dataset.nativeReferenceVisible === 'true';

          if (!memberLine) {
            lineItem.classList.remove('line-item--min-guru-pricing');
            compactReference.textContent =
              compactPrice.dataset.nativeReference || '';
            compactReference.hidden = !nativeReferenceVisible;
            return;
          }

          const compareAtFromTheme =
            Number(lineItem.dataset.minGuruCompareAtPrice || 0) / 100;
          const normalPrice = Math.max(
            memberLine.compareAt,
            compareAtFromTheme,
            memberLine.shopPrice,
          );
          const referencePrice = Math.max(
            normalPrice,
            memberLine.shopPrice,
          );
          lineItem.classList.add('line-item--min-guru-pricing');
          compactReference.textContent = money(
            referencePrice * memberLine.quantity,
          );
          compactReference.hidden =
            referencePrice <= memberLine.memberPrice;
          return;
        }

        if (!memberLine) {
          lineItem.classList.remove('line-item--min-guru-pricing');
          existing?.remove();
          existingBadge?.remove();
          return;
        }

        const compareAtFromTheme =
          Number(lineItem.dataset.minGuruCompareAtPrice || 0) / 100;
        const normalPrice = Math.max(
          memberLine.compareAt,
          compareAtFromTheme,
          memberLine.shopPrice,
        );
        const hasSalePrice = normalPrice > memberLine.shopPrice;
        const totalSaving = roundCurrency(
          normalPrice - memberLine.memberPrice,
        );
        const savingPercent =
          normalPrice > 0
            ? Math.round((totalSaving / normalPrice) * 100)
            : 0;
        const headline = lineItem.querySelector('.line-item__headline');
        const nativePriceList = headline?.querySelector('price-list');
        if (!headline || !nativePriceList) return;

        lineItem.classList.add('line-item--min-guru-pricing');

        const badge = existingBadge || document.createElement('span');
        if (!existingBadge) {
          badge.className =
            'badge badge--on-sale line-item__rebate-badge min-guru-member-savings';
          badge.dataset.minGuruMemberSavings = 'true';
          nativePriceList.before(badge);
        }
        badge.textContent = `Spar ${savingPercent}% i alt`;

        const container = existing || document.createElement('div');
        container.className = 'min-guru-member-price-ladder';
        container.dataset.minGuruMemberPricing = 'true';
        container.setAttribute(
          'aria-label',
          memberLine.quantity > 1 ? 'Priser pr. stk.' : 'Priser',
        );
        container.replaceChildren();

        if (memberLine.quantity > 1) {
          const unit = document.createElement('p');
          unit.className = 'min-guru-member-price-unit';
          unit.textContent = 'Pris pr. stk.';
          container.append(unit);
        }

        const list = document.createElement('dl');
        if (hasSalePrice) {
          list.append(
            priceRow(
              'Normalpris',
              normalPrice,
              'min-guru-member-price-row--muted',
            ),
          );
        }
        list.append(
          priceRow(
            hasSalePrice ? 'Tilbudspris' : 'Normalpris',
            memberLine.shopPrice,
            'min-guru-member-price-row--muted',
          ),
          priceRow(
            'Medlemspris',
            memberLine.memberPrice,
            'min-guru-member-price-row--member',
          ),
        );

        const discount = document.createElement('div');
        discount.className = 'min-guru-member-line-discount';
        const discountLabel = document.createElement('span');
        const discountValue = document.createElement('strong');
        discountLabel.textContent = `Min Guru medlemsrabat${
          memberLine.quantity > 1 ? ' i alt' : ''
        }`;
        discountValue.textContent = `−${money(
          memberLine.discountAmount,
        )}`;
        discount.append(discountLabel, discountValue);
        container.append(list, discount);

        if (!existing) headline.append(container);
      });
  };

  const renderTotals = () => {
    document
      .querySelectorAll('[data-min-guru-cart-total]')
      .forEach((element) => {
        element.dataset.minGuruNativeText ||= element.textContent.trim();
        element.textContent =
          latestMemberTotal !== null && latestDiscountAmount > 0
            ? money(latestMemberTotal)
            : element.dataset.minGuruNativeText;
      });

    document
      .querySelectorAll('[data-min-guru-line-total]')
      .forEach((element) => {
        element.dataset.minGuruNativeText ||= element.textContent.trim();
        const commerceIdentity =
          element.dataset.commerceIdentity ||
          element.closest('line-item')?.dataset.commerceIdentity ||
          '';
        const memberLine = latestMemberLines.find(
          (line) => line.commerceIdentity === commerceIdentity,
        );
        element.textContent = memberLine
          ? money(memberLine.memberLineTotal)
          : element.dataset.minGuruNativeText;
      });
  };

  const renderMemberPricing = () => {
    renderLinePricing();
    renderDiscount();
    renderTotals();
    document.querySelectorAll('[data-min-guru-stacking-policy]').forEach(
      (policy) => { policy.hidden = !latestStackingBlocked; },
    );
  };

  const drawerRoots = () => [
    ...document.querySelectorAll(cartRootSelector),
  ];

  const drawerRootsAreCurrent = (roots) => {
    const currentRoots = drawerRoots();
    return (
      roots.length === currentRoots.length &&
      roots.every(
        (root, index) =>
          root.isConnected && root === currentRoots[index],
      )
    );
  };

  const memberSyncIsCurrent = (sequence, signal, roots) =>
    sequence === syncSequence &&
    !signal?.aborted &&
    roots === activeSyncRoots &&
    drawerRootsAreCurrent(roots);

  const invalidateMemberSync = () => {
    syncSequence += 1;
    window.clearTimeout(scheduledSync);
    scheduledSync = undefined;
    activeSyncController?.abort();
    activeSyncController = null;
    activeSyncRoots = [];
    return syncSequence;
  };

  const readNativeCart = async (signal) => {
    const response = await fetch(
      `${window.Shopify?.routes?.root || '/'}cart.js`,
      {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal,
      },
    );
    if (!response.ok) throw new Error('Cart could not be read.');
    return response.json();
  };

  const quoteMemberCart = async (cart, sequence, signal, roots) => {
    if (hasUnsupportedCartState(cart)) {
      if (!memberSyncIsCurrent(sequence, signal, roots)) return null;
      clearMemberPricing({ stackingBlocked: authenticated });
      renderMemberPricing();
      return { status: 'unsupported' };
    }

    const nativeAmount = nativeMemberDiscountAmount(cart);
    if (nativeAmount > 0) {
      if (!memberSyncIsCurrent(sequence, signal, roots)) return null;
      latestDiscountAmount = nativeAmount / 100;
      latestMemberLines = nativeMemberLines(cart);
      latestMemberTotal = Number(cart.total_price || 0) / 100;
      latestCheckoutUrl = '';
      latestStackingBlocked = false;
      renderMemberPricing();
      return { status: 'native' };
    }

    const lines = portalLines(cart);
    if (lines.length === 0) {
      if (!memberSyncIsCurrent(sequence, signal, roots)) return null;
      clearMemberPricing();
      renderMemberPricing();
      return { status: 'empty' };
    }

    const response = await fetch(`${portalOrigin}${portalPath}/api/cart`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action: 'replace', lines }),
      signal,
    });
    if (!response.ok) throw new Error('Member cart could not be priced.');

    const memberCart = await response.json();
    if (!memberSyncIsCurrent(sequence, signal, roots)) return null;
    const integrity = validateMemberQuote(memberCart, cart);
    if (!integrity) {
      clearMemberPricing();
      renderMemberPricing();
      return { status: 'invalid' };
    }
    const quotedDiscountAmount = integrity.discountAmount;
    const quotedMemberLines = portalMemberLines(memberCart, cart);
    const discountedLineCount = (memberCart?.lines || []).filter(
      (line) => lineMemberDiscountAmount(line.discountAllocations) > 0,
    ).length;
    if (quotedMemberLines.length !== discountedLineCount) {
      clearMemberPricing({ stackingBlocked: true });
      renderMemberPricing();
      return { status: 'invalid' };
    }
    latestDiscountAmount = quotedDiscountAmount;
    latestMemberLines = quotedMemberLines;
    latestMemberTotal =
      latestDiscountAmount > 0 ? integrity.subtotal : null;
    latestCheckoutUrl =
      latestDiscountAmount > 0
        ? integrity.checkoutUrl
        : '';
    latestStackingBlocked = false;
    renderMemberPricing();
    return {
      status: 'valid',
      cart: memberCart,
      checkoutUrl: integrity.checkoutUrl,
      discountAmount: integrity.discountAmount,
    };
  };

  const syncMemberCart = async (cart, claimedSequence) => {
    if (!authenticated) return null;
    const sequence = claimedSequence ?? invalidateMemberSync();
    if (sequence !== syncSequence) return null;
    const controller = new AbortController();
    const roots = drawerRoots();
    activeSyncController = controller;
    activeSyncRoots = roots;
    try {
      const result = await quoteMemberCart(
        cart || (await readNativeCart(controller.signal)),
        sequence,
        controller.signal,
        roots,
      );
      return memberSyncIsCurrent(sequence, controller.signal, roots)
        ? result
        : null;
    } catch (error) {
      if (error?.name === 'AbortError') return null;
      if (memberSyncIsCurrent(sequence, controller.signal, roots)) {
        clearMemberPricing();
        renderMemberPricing();
      }
      return { status: 'invalid' };
    } finally {
      if (activeSyncController === controller) {
        activeSyncController = null;
        activeSyncRoots = [];
      }
    }
  };

  const scheduleMemberCart = (cart) => {
    const sequence = invalidateMemberSync();
    if (!authenticated) return;
    scheduledSync = window.setTimeout(() => {
      scheduledSync = undefined;
      void syncMemberCart(cart, sequence);
    }, 120);
  };

  const nativeCheckoutUrl = () =>
    `${window.Shopify?.routes?.root || '/'}checkout`;

  const navigateCheckout = (url) => {
    const navigation = new CustomEvent('min-guru:checkout:navigate', {
      cancelable: true,
      detail: { url },
    });
    if (document.dispatchEvent(navigation)) window.location.assign(url);
  };

  const onCheckout = async (event) => {
    const form = event.target;
    if (
      !(form instanceof HTMLFormElement) ||
      event.submitter?.name !== 'checkout'
    ) {
      return;
    }
    const approvedDrawer =
      form.closest(cartRootSelector);

    if (!sessionResolved) {
      event.preventDefault();
      event.stopImmediatePropagation();
      setSessionState('resolving');
      return;
    }

    if (activeCommerceOperations > 0) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (approvedDrawer?.dataset.guruCartBlocked === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (!authenticated) return;

    const action = new URL(form.action, window.location.href);
    if (!action.pathname.endsWith('/cart')) return;
    const unsavedNote = form.querySelector(
      'textarea[name="note"]',
    )?.value;
    if (typeof unsavedNote === 'string' && unsavedNote.trim()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutInFlight) return;

    const attempt = ++checkoutAttempt;
    const revision = cartRevision;

    const checkout = form.querySelector('[name="checkout"]');
    const setCheckoutBusy = (busy) => {
      if (approvedDrawer) {
        approvedDrawer.dataset.minGuruCheckoutBusy = String(busy);
        window.GuruShopCartDrawer?.refresh?.();
      }
      if (checkout) {
        checkout.disabled = busy;
        checkout.setAttribute('aria-busy', String(busy));
      }
    };

    setCheckoutBusy(true);
    invalidateMemberSync();
    checkoutInFlight = (async () => {
      const cart = await readNativeCart().catch(() => null);
      if (attempt !== checkoutAttempt || revision !== cartRevision) return;
      if (!cart) {
        setCheckoutBusy(false);
        return;
      }
      if (nativeMemberDiscountAmount(cart) > 0) {
        setCheckoutBusy(false);
        if (attempt !== checkoutAttempt || revision !== cartRevision) return;
        navigateCheckout(nativeCheckoutUrl());
        return;
      }

      const quote = await syncMemberCart(cart);
      if (attempt !== checkoutAttempt || revision !== cartRevision) return;
      setCheckoutBusy(false);
      if (approvedDrawer?.dataset.guruCartBlocked === 'true') return;
      if (quote?.status === 'unsupported') {
        navigateCheckout(nativeCheckoutUrl());
        return;
      }
      if (quote?.status !== 'valid') {
        const status = approvedDrawer?.querySelector(
          '[data-guru-cart-status]',
        );
        if (status) {
          status.textContent =
            'Medlemsprisen kunne ikke bekræftes. Prøv betaling igen.';
        }
        return;
      }
      navigateCheckout(
        quote.discountAmount > 0
          ? quote.checkoutUrl
          : nativeCheckoutUrl(),
      );
    })().finally(() => {
      checkoutInFlight = null;
      setCheckoutBusy(false);
    });
    await checkoutInFlight;
  };

  const enable = async () => {
    try {
      const response = await fetch(
        `${portalOrigin}${portalPath}/api/session`,
        {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        },
      );
      const session = response.ok ? await response.json() : null;
      authenticated = Boolean(session?.authenticated);
      setSessionState('resolved');
      if (authenticated) {
        scheduleMemberCart();
      } else {
        clearMemberPricing();
        renderMemberPricing();
      }
    } catch {
      authenticated = false;
      setSessionState('resolved');
      clearMemberPricing();
      renderMemberPricing();
    }
  };

  const invalidateCheckoutForMutation = () => {
    cartRevision += 1;
    if (checkoutInFlight) checkoutAttempt += 1;
  };

  setSessionState('resolving');

  document.addEventListener('theme:loading:start', () => {
    activeCommerceOperations += 1;
    invalidateCheckoutForMutation();
    syncDrawerRuntimeState();
  });
  document.addEventListener('theme:loading:end', () => {
    activeCommerceOperations = Math.max(0, activeCommerceOperations - 1);
    invalidateCheckoutForMutation();
    syncDrawerRuntimeState();
  });
  document.addEventListener('cart:change', (event) => {
    drawerRefreshPending = true;
    invalidateCheckoutForMutation();
    syncDrawerRuntimeState();
    scheduleMemberCart(event.detail?.cart);
  });
  document.addEventListener('cart:refresh', () => {
    invalidateCheckoutForMutation();
    scheduleMemberCart();
  });
  document.addEventListener('cart-drawer:refreshed', () => {
    drawerRefreshPending = false;
    invalidateMemberSync();
    clearMemberPricing();
    syncDrawerRuntimeState();
    renderMemberPricing();
    if (authenticated) scheduleMemberCart();
  });
  document.addEventListener('guru-cart-page:refreshed', (event) => {
    drawerRefreshPending = false;
    invalidateMemberSync();
    clearMemberPricing();
    const root = event.target.matches?.(cartRootSelector)
      ? event.target
      : event.target.closest?.(cartRootSelector);
    initializeDrawerRoot(root);
    syncDrawerRuntimeState();
    renderMemberPricing();
    if (authenticated) scheduleMemberCart();
  });
  document.addEventListener('shopify:section:load', (event) => {
    invalidateMemberSync();
    const roots = event.target.matches?.(cartRootSelector)
      ? [event.target]
      : [...(event.target.querySelectorAll?.(cartRootSelector) || [])];
    roots.forEach(initializeDrawerRoot);
    window.GuruShopCartDrawer?.refresh?.();
    renderMemberPricing();
    if (authenticated) scheduleMemberCart();
  });
  document.addEventListener('shopify:section:unload', (event) => {
    if (
      event.target.matches?.(cartRootSelector) ||
      event.target.querySelector?.(cartRootSelector)
    ) {
      invalidateMemberSync();
      clearMemberPricing();
      renderMemberPricing();
      invalidateCheckoutForMutation();
      if (activeCommerceOperations === 0) drawerRefreshPending = false;
    }
  }, true);
  document.addEventListener('submit', onCheckout, true);

  window[namespaceKey] = {
    calculateSavings: aggregateSavingsMinor,
    refresh: () => {
      invalidateMemberSync();
      clearMemberPricing();
      setSessionState(sessionResolved ? 'resolved' : 'resolving');
      renderMemberPricing();
      scheduleMemberCart();
    },
  };

  void enable();
})();
